import m, {MithrilComponent, state} from '@bitstillery/mithril'
import {Button, Icon} from '@bitstillery/common/components/portal'
import {present} from '@bitstillery/common/lib/utils'

export interface FunFact {
    /** Photo URL — a CMS media-library public URL, a static `/img/…` path, or an external URL. */
    image: string
    /** Short teaser shown under the preview photo. */
    teaser?: string
    /** Modal heading. */
    title?: string
    /** Modal body copy. */
    body?: string
    /** Accessible image description. */
    alt?: string
}

export interface FunFactsAttrs {
    /** The facts to cycle through; each opens a modal with its title/body/image. */
    facts: FunFact[]
}

interface FunFactsData {
    index: number
    open_index: number | null
}

/**
 * Content-driven fun-fact widget: a circular photo with a "next" control and a teaser, opening a modal
 * with the full fact. Unlike the code footer's media-folder/i18n version, every fact — image, teaser,
 * title, body — is authored inline via the `facts` prop, so staff edit them straight from a fragment.
 * Reuses the footer fun-fact + modal styles (`c-footer-fun-facts`, `footer-fun-fact-modal-*`).
 */
export class FunFacts extends MithrilComponent<FunFactsAttrs> {
    data: FunFactsData = state(
        {
            index: 0,
            open_index: null,
        },
        'cms-fun-facts',
    )

    view(vnode: m.Vnode<FunFactsAttrs>) {
        const facts = Array.isArray(vnode.attrs.facts) ? vnode.attrs.facts : []
        if (facts.length === 0) {
            return null
        }

        const count = facts.length
        const index = ((this.data.index % count) + count) % count
        const preview = present(facts[index], 'fun fact')
        const open = this.data.open_index != null ? facts[this.data.open_index] : null

        return (
            <section className='c-footer-fun-facts' aria-label='Fun facts'>
                <div className='fun-fact-visual'>
                    <button
                        type='button'
                        className='fun-fact-photo'
                        style={preview.image ? {backgroundImage: `url(${preview.image})`} : undefined}
                        aria-haspopup='dialog'
                        aria-label={preview.alt ?? preview.title ?? 'Fun fact'}
                        onclick={() => {
                            this.data.open_index = index
                        }}
                    />
                    {count > 1 ? (
                        <button
                            type='button'
                            className='fun-fact-next'
                            aria-label='Next fun fact'
                            onclick={() => {
                                this.data.index = index + 1
                            }}
                        >
                            <Icon name='chevronRight' size='s' type='unset' />
                        </button>
                    ) : null}
                </div>
                {preview.teaser ? <p className='fun-fact-quote'>{preview.teaser}</p> : null}
                {open ? (
                    <div className='footer-fun-fact-modal-backdrop' role='presentation'>
                        <div className='footer-fun-fact-modal' role='dialog'>
                            <Button
                                className='footer-fun-fact-modal-close'
                                icon='close'
                                onclick={() => {
                                    this.data.open_index = null
                                }}
                                size='l'
                                variant='icon'
                            />
                            <div className='footer-fun-fact-modal-text'>
                                {open.title ? <h2 className='footer-fun-fact-modal-title'>{open.title}</h2> : null}
                                {open.body ? <p className='footer-fun-fact-modal-body'>{open.body}</p> : null}
                                {count > 1 ? (
                                    <button
                                        type='button'
                                        className='footer-fun-fact-modal-next'
                                        aria-label='Next fun fact'
                                        onclick={() => {
                                            const next = ((this.data.open_index ?? 0) + 1) % count
                                            this.data.open_index = next
                                            this.data.index = next
                                        }}
                                    >
                                        <Icon name='chevronRight' size='s' type='unset' />
                                    </button>
                                ) : null}
                            </div>
                            <div className='footer-fun-fact-modal-image-wrap'>
                                <img
                                    alt={open.alt ?? ''}
                                    className='footer-fun-fact-modal-image'
                                    decoding='async'
                                    loading='lazy'
                                    src={open.image}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
            </section>
        )
    }
}
